import React, { useState } from 'react';
import { X, ChevronDown, Check, Plus, Clock, Globe } from 'lucide-react';
import { colors } from '../../../../styles/tokens';

export interface EmailAccount {
  id: string;
  email: string;
  name: string;
  provider: string;
}

interface Timezone {
  value: string;
  label: string;
  offset: string;
}

interface SettingsModalProps {
  isOpen: boolean;
  emailAccounts: EmailAccount[];
  editSendWindowStart: string;
  editSendWindowEnd: string;
  editSendDays: string[];
  selectedEmailAccount: string;
  selectedTimezone: string;
  onClose: () => void;
  onSendWindowStartChange: (value: string) => void;
  onSendWindowEndChange: (value: string) => void;
  onEmailAccountChange: (id: string) => void;
  onTimezoneChange: (value: string) => void;
  onSave: () => void;
  onOpenEmailSettings: () => void;
}

const timezones: Timezone[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8' },
  { value: 'America/Phoenix', label: 'Arizona Time', offset: 'UTC-7' },
  { value: 'Europe/London', label: 'London (GMT)', offset: 'UTC+0' },
  { value: 'Europe/Paris', label: 'Central European', offset: 'UTC+1' },
  { value: 'Asia/Tokyo', label: 'Japan Time', offset: 'UTC+9' },
  { value: 'Asia/Singapore', label: 'Singapore Time', offset: 'UTC+8' },
  { value: 'Australia/Sydney', label: 'Sydney Time', offset: 'UTC+11' },
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  emailAccounts,
  editSendWindowStart,
  editSendWindowEnd,
  editSendDays,
  selectedEmailAccount,
  selectedTimezone,
  onClose,
  onSendWindowStartChange,
  onSendWindowEndChange,
  onEmailAccountChange,
  onTimezoneChange,
  onSave,
  onOpenEmailSettings,
}) => {
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);

  if (!isOpen) return null;

  const getDaysLabel = () => {
    if (editSendDays.length === 5 && ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].every(d => editSendDays.includes(d)) && !editSendDays.includes('Sat') && !editSendDays.includes('Sun')) {
      return 'Weekdays only';
    } else if (editSendDays.length === 7) {
      return 'Every day';
    } else if (editSendDays.length === 0) {
      return 'No days selected';
    }
    return `${editSendDays.length} days selected`;
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[200] animate-fadeIn"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[201] flex items-end sm:items-center justify-center sm:p-4">
        <div className="bg-white rounded-t-xl sm:rounded-2xl shadow-2xl w-full sm:max-w-[420px] max-h-[85vh] overflow-hidden animate-fadeInScale flex flex-col">
          <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[15px] sm:text-[16px] font-semibold" style={{ color: colors.neutral[900] }}>Sending Settings</h3>
              <button
                onClick={onClose}
                className="p-1.5 -mr-1.5 rounded-lg transition-colors hover:bg-neutral-100"
                style={{ color: colors.neutral[400] }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[12px] sm:text-[13px]" style={{ color: colors.neutral[500] }}>Configure when and how emails are sent</p>
          </div>

          <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-5 sm:space-y-6 overflow-y-auto flex-1">
            <div className="relative">
              <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: colors.neutral[400] }}>Send From</label>
              <button
                onClick={() => setShowEmailDropdown(!showEmailDropdown)}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-xl transition-all text-left group"
                style={{ backgroundColor: colors.neutral[50], border: `1px solid ${colors.neutral[100]}` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ background: `linear-gradient(to bottom right, ${colors.neutral[700]}, ${colors.neutral[900]})` }}
                  >
                    <span className="text-white text-[12px] font-semibold">
                      {(emailAccounts.find(a => a.id === selectedEmailAccount)?.email?.charAt(0)?.toUpperCase()) ?? ''}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: colors.neutral[900] }}>
                      {emailAccounts.find(a => a.id === selectedEmailAccount)?.email}
                    </p>
                    <p className="text-[11px]" style={{ color: colors.neutral[500] }}>
                      {emailAccounts.find(a => a.id === selectedEmailAccount)?.name}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 transition-all ${showEmailDropdown ? 'rotate-180' : ''}`} style={{ color: colors.neutral[400] }} />
              </button>

              {showEmailDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-neutral-200 shadow-xl z-10 overflow-hidden">
                  <div className="p-1">
                    {emailAccounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => {
                          onEmailAccountChange(account.id);
                          setShowEmailDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedEmailAccount === account.id ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-neutral-900 truncate">{account.email}</p>
                          <p className="text-[11px] text-neutral-500">{account.name}</p>
                        </div>
                        {selectedEmailAccount === account.id && (
                          <Check className="w-4 h-4 text-neutral-900 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-neutral-100 p-1">
                    <button
                      onClick={() => {
                        setShowEmailDropdown(false);
                        onClose();
                        onOpenEmailSettings();
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add email account
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4 relative">
              <div className="flex items-center gap-2">
                <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wide">Schedule</label>
                <span className="text-[10px] font-medium text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">Coming soon</span>
              </div>

              <div className="opacity-50 pointer-events-none space-y-4">
                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                  <Globe className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                  <select
                    value={selectedTimezone}
                    onChange={(e) => onTimezoneChange(e.target.value)}
                    disabled
                    className="flex-1 text-[13px] text-neutral-700 bg-transparent focus:outline-none font-medium"
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label} ({tz.offset})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                    <Clock className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <select
                      value={editSendWindowStart}
                      onChange={(e) => onSendWindowStartChange(e.target.value)}
                      disabled
                      className="flex-1 text-[13px] text-neutral-700 bg-transparent focus:outline-none font-medium"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i % 12 || 12;
                        const ampm = i < 12 ? 'AM' : 'PM';
                        return (
                          <option key={i} value={`${hour}:00 ${ampm}`}>{`${hour}:00 ${ampm}`}</option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="w-8 h-px bg-neutral-300" />
                  <div className="flex-1 flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                    <Clock className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <select
                      value={editSendWindowEnd}
                      onChange={(e) => onSendWindowEndChange(e.target.value)}
                      disabled
                      className="flex-1 text-[13px] text-neutral-700 bg-transparent focus:outline-none font-medium"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i % 12 || 12;
                        const ampm = i < 12 ? 'AM' : 'PM';
                        return (
                          <option key={i} value={`${hour}:00 ${ampm}`}>{`${hour}:00 ${ampm}`}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <button
                      key={day}
                      disabled
                      className={`aspect-square rounded-xl text-[11px] font-semibold transition-all ${
                        editSendDays.includes(day)
                          ? 'bg-neutral-900 text-white shadow-sm'
                          : 'bg-neutral-50 text-neutral-400 border border-neutral-100'
                      }`}
                    >
                      {day.charAt(0)}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-neutral-400 text-center">
                  {getDaysLabel()}
                </p>
              </div>
            </div>
          </div>

          <div
            className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-end gap-2 flex-shrink-0"
            style={{ backgroundColor: colors.neutral[50], borderTop: `1px solid ${colors.neutral[100]}` }}
          >
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-medium rounded-lg transition-colors hover:bg-neutral-100"
              style={{ color: colors.neutral[600] }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave();
                onClose();
              }}
              className="px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: colors.neutral[900] }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
